import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { autoinject } from "aurelia-dependency-injection";
import { bindable } from "aurelia-templating";
import { DueDiligenceDialogConstants } from "../../../constants/dialog.constants";
import { DueDiligenceApi } from "../../../apis/due-diligence.api";
import { CustomerDueDiligenceApprovalEmailModel, VetRecentActivityClientModel, VetStatusEnum, VetStatusHistoryClientModel } from "../../../models/due-diligence.models";
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { computedFrom } from "aurelia-binding";
import moment from "moment";
import { type VesselDetailModel } from "../../../models/vessel.models";
import { SeniorApprovalApi } from "../../../apis/senior-approval.api";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { Reflection } from "@rightship-tech/common-ui/helpers/reflection";
import { GoogleAnalyticsService } from "@rightship-tech/common-ui/services/analytics/google-analytics.service";
import { DueDiligenceHelper } from "@rightship-tech/common-ui/helpers/due-diligence/due-diligence.helper";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { DueDiligenceClaims } from "../../../constants/claims.constants";

//Load styles
require("./comp-due-diligence-status-card.scss");

@autoinject
export class CompDueDiligenceStatusCard {

    private _seniorApprovers: CustomerDueDiligenceApprovalEmailModel[] = [];
    public isLoading: boolean = true;
    public isShowingStatusHistory: boolean;

    @bindable
    public status: VetStatusEnum;

    @bindable
    public vetId: number;

    @bindable
    public statusHistory: VetStatusHistoryClientModel[];

    @bindable
    public activities: VetRecentActivityClientModel[] = [];

    @bindable
    public requestedBy: Date;

    @bindable
    public createdDate: Date;

    @bindable
    public outcomeRequiredIn: Date;

    @bindable
    public isMinimised: boolean;

    @bindable
    public vessel: VesselDetailModel;

    @bindable
    public customerId?: number;

    constructor(private dialogClientService: DialogClientService, private dueDiligenceApi: DueDiligenceApi,
        private notificationClientService: NotificationClientService, private seniorApprovalApi: SeniorApprovalApi,
        private userClientService: UserClientService, private googleAnalyticsService: GoogleAnalyticsService,
        private userClaimsService: UserClaimsService
    ) {
        this.isLoading = true;
    }

    @computedFrom(Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x.status))
    public get underReview() {
        return this.status == VetStatusEnum.DD_UnderReview;
    }

    @computedFrom(Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x.status))
    public get pendingSeniorApproval() {
        return this.status == VetStatusEnum.DD_PendingSeniorApproval;
    }

    @computedFrom("outcomeRequiredIn")
    public get showDueDiligenceOutcomeRequiredBy(): boolean {
        return this.userClaimsService.hasPermission(DueDiligenceClaims.ShowDueDiligenceOutcomeRequiredBy)
            && this.outcomeRequiredByDateCountdown?.length > 0;
    }
    
    @computedFrom("statusHistory", "statusHistory.length")
    public get getLatestUpdateText() {
        if (this.sortedStatusHistory && this.sortedStatusHistory.length > 0) {
            let latest = this.sortedStatusHistory[0]
            let date = moment(latest.createdDate).format("DD MMM YYYY, HH:mmA")
            let createdBy = latest.createdBy && latest.createdBy.length > 0? latest.createdBy : 'System'
            return `${date} by ${createdBy}`;
        }

        return `${moment(this.createdDate).format("DD MMM YYYY, HH:mmA")} by ${this.requestedBy ?? 'System'}`;
    }

    private _sortedStatusHistory: VetStatusHistoryClientModel[];

    @computedFrom("statusHistory", "statusHistory.length")
    public get sortedStatusHistory() {
        
        //Sort status history
        if (this.statusHistory && this.statusHistory.length > 0 && this._sortedStatusHistory?.length != this.statusHistory.length) {
            this._sortedStatusHistory = this.statusHistory.sort((a, b) => {
                return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
            });
        }

        return this._sortedStatusHistory;
    }

    @computedFrom("statusHistory", "statusHistory.length")
    public get getLatestStatusSnippet() {
        if (this.sortedStatusHistory && this.sortedStatusHistory.length > 0) {
            // do not show the status snippet if the status is a terminal one and it was set by an automated process (ET1-1601)
            if (this.canShowSnippet(this.sortedStatusHistory[0])) {
                return this.sortedStatusHistory[0].comment;
            }
            return "";            
        }
    }

    @computedFrom(
        Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x._seniorApprovers),
        Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x._seniorApprovers.length),
        Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x.userClientService.currentUser),
        Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x.userClientService.currentUser.user),
        Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x.userClientService.currentUser.user.email)
    )
    public get isSeniorApprover() {
        return this._seniorApprovers?.filter(x => x.email == this.userClientService?.currentUser?.user?.email)?.length > 0;
    }    

    @computedFrom(Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x.status),
        Reflection.getPropertyPath<CompDueDiligenceStatusCard>(x => x.isSeniorApprover)
    )

    public get canReview()
    {
        return (!this.pendingSeniorApproval || (this.pendingSeniorApproval && this.isSeniorApprover))
            && !(this.status == VetStatusEnum.DD_Unacceptable && 
                this.userClaimsService.hasPermission(DueDiligenceClaims.DueDiligenceHideReviewButtonWhenUnacceptable)) &&
                this.userClaimsService.hasPermission(DueDiligenceClaims.CanReviewDueDiligence);
    }

    public get canRequestSeniorApproval()
    {
        return this.userClaimsService.hasPermission(DueDiligenceClaims.CanRequestSeniorApproval);
    }


    @computedFrom("status")
    get getStatus() {
        switch (this.status) {
            case VetStatusEnum.DD_Acceptable:
            return "Acceptable";
            case VetStatusEnum.DD_Unacceptable:
            return "Unacceptable";
            default:
            return "";
        }
    }

    async attached() 
    {
        //Get senior approvers
        if (this.customerId) {
            var seniorApproverResponse = await this.seniorApprovalApi.getCustomerSeniorApprovers(this.customerId);
            this._seniorApprovers = seniorApproverResponse?.seniorApprovers;
        }

        this.isLoading = false;
    }

    public canShowSnippet(historyItem: VetStatusHistoryClientModel): boolean {
        var terminalStatuses = [VetStatusEnum.DD_Acceptable];
        var automatedUsers = ["system", "guest"];
        if (terminalStatuses.includes(historyItem.statusId)
            && (!historyItem.createdBy || automatedUsers.includes(historyItem.createdBy?.toLowerCase()))) {           
            return false;
        }
        return true;
    }

    //Show status history
    public showStatusHistory() {
        this.isShowingStatusHistory = !this.isShowingStatusHistory;
    }

    //Set due diligence status to Under Review
    public async review() {
        try {
            await this.dueDiligenceApi.changeStatus(this.vetId, "", "", VetStatusEnum.DD_UnderReview);
            this.notificationClientService.show("Due diligence status was successfully changed to Under Review", "", NotificationType.Success);
        }
        catch (error) {
            console.error("An error occurred while changing due diligence status", error);
            this.notificationClientService.show("An error occurred while changing due diligence status", "", NotificationType.Error);
        }
    }

    //Update due diligence status dialog
    public updateStatus() {

        //Send event to google analytics
        this.googleAnalyticsService.sendCustomGA4EventMulti("ddh_update_due_diligence_status_manually", {
            "vesselName": this.vessel.name ?? "N/a",
            "vesselImo": this.vessel.internationalMaritimeOrganizationNumber ?? "N/a",
            "vesselType": this.vessel.vesselType ?? "N/a"
        });

        //Open dialog
        this.dialogClientService.show(DueDiligenceDialogConstants.DueDiligenceStatusDialog,
            {
                dueDiligenceId: this.vetId,
                dueDiligenceStatusId: this.status,
                vesselName: this.vessel.name,
                internationalMaritimeOrganizationNumber: this.vessel.internationalMaritimeOrganizationNumber,
                europeanNumberOfIdentification: this.vessel.europeanNumberOfIdentification,
                vehicleIdentificationNumber: this.vessel.vehicleIdentificationNumber,
                dwt: this.vessel.dwt,
                vesselType: this.vessel.vesselTypeId,
            }, true, false, false, 'right');
    }

    //Get outcome required by date countdown
    public get outcomeRequiredByDateCountdown(): string {
        if (this.outcomeRequiredIn) {
            const now = moment();
            const outcomeRequiredBy = moment(this.outcomeRequiredIn);
            if (outcomeRequiredBy.isAfter(now) && this.status != VetStatusEnum.DD_Acceptable && this.status != VetStatusEnum.DD_Unacceptable) {
                const duration = moment.duration(outcomeRequiredBy.diff(now));
                const days = Math.floor(duration.asDays());
                const hours = duration.hours();
                const minutes = duration.minutes();
                const seconds = duration.seconds();
                const parts = [];
                if (days > 0) parts.push(`${days}d`);
                if (days > 0 || hours > 0) parts.push(`${hours}h`);
                if (days > 0 || hours > 0 || minutes > 0) parts.push(`${minutes}m`);
                if (days > 0 || hours > 0 || minutes > 0 || seconds > 0) parts.push(`${seconds}s`);
                return parts.join(' ');
            }
            
            return moment(outcomeRequiredBy).format("DD MMM YYYY HH:mm");
        }
        return '';
    }

    //Generate recent link
    public generateRecentLink(title: string, id: number, type: string) {
        return this.dueDiligenceApi.generateRecentLink(this.vetId, title, id, type);
    }

    //Require senior approval
    public async requireSeniorApproval() {
        if (this._seniorApprovers.length == 0) {
            this.dialogClientService.show(DueDiligenceDialogConstants.NoSeniorApprovalDialog, {});
        } 
        else {

            //Send event to google analytics
            this.googleAnalyticsService.sendCustomGA4EventMulti("ddh_senior_approval_require", {
                "vesselName": this.vessel.name ?? "N/a",
                "vesselImo": this.vessel.internationalMaritimeOrganizationNumber ?? "N/a",
                "vesselType": this.vessel.vesselType ?? "N/a",
                "dueDiligenceStatus": DueDiligenceHelper.getStatus(this.status)
            });

            //Show dialog
            this.dialogClientService.show(DueDiligenceDialogConstants.SeniorApprovalDialog,
                {
                    dueDiligenceId: this.vetId,
                    vessel: this.vessel
                }, true, false, false, 'right');
        }
    }
}